import {
  HttpTransportType,
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
  type IRetryPolicy,
  type RetryContext,
} from '@microsoft/signalr'
import { useConnection } from '../state/connection'

/** How long the first reconnect waits at most, at random: a pod going away drops all its clients at once, and they should not all come back in the same second. */
export const FIRST_RETRY_JITTER_MS = 3000

/** Backoff that never gives up: 0–3 s, then 2, 5, 10, 20 s, then every 30 s (with jitter). */
export const foreverRetryPolicy: IRetryPolicy = {
  nextRetryDelayInMilliseconds(context: RetryContext): number {
    const steps = [0, 2000, 5000, 10000, 20000]
    const base = steps[context.previousRetryCount] ?? 30000
    return base === 0 ? Math.floor(Math.random() * FIRST_RETRY_JITTER_MS) : base + Math.floor(Math.random() * 1000)
  },
}

/** Hidden at least this long, a connection that says it is up is not trusted: phones kill sockets in the background without closing them. */
export const STALE_AFTER_HIDDEN_MS = 10_000

/** Silence from the server after which the connection counts as dead (the server pings every 10 s: api Program.cs). */
export const SERVER_TIMEOUT_MS = 20_000

export type ResumeAction = 'none' | 'wake' | 'replace'

/**
 * What to do when the app comes back to the front or the network returns. `wake` cuts short the wait before the next
 * first-connection attempt; `replace` drops the connection and connects afresh, because it may be dead without knowing
 * (connected, but away long enough) or is waiting out a reconnect backoff that no longer has a reason.
 */
export function resumeAction(state: HubConnectionState | null, trustConnected: boolean): ResumeAction {
  switch (state) {
    case null:
    case HubConnectionState.Connecting:
      return 'none'
    case HubConnectionState.Disconnected:
      return 'wake'
    case HubConnectionState.Connected:
      return trustConnected ? 'none' : 'replace'
    default:
      return 'replace'
  }
}

export interface HubCallbacks {
  register: (connection: HubConnection) => void
  onConnected: (isReconnect: boolean) => void
}

let connection: HubConnection | null = null
let startLoop: Promise<void> | null = null
let stopped = false
let callbacks: HubCallbacks | null = null
/** Connections dropped on purpose by reconnectNow: their close must not start another loop. */
const replaced = new WeakSet<HubConnection>()
/** Ends the start loop's current wait early. */
let wake: (() => void) | null = null

function build(): HubConnection {
  const conn = new HubConnectionBuilder()
    .withUrl('/hubs/chat', { transport: HttpTransportType.WebSockets, skipNegotiation: true })
    .withAutomaticReconnect(foreverRetryPolicy)
    .withServerTimeout(SERVER_TIMEOUT_MS)
    .configureLogging(LogLevel.Warning)
    .build()

  conn.onreconnecting(() => useConnection.getState().setHub('reconnecting'))
  conn.onreconnected(() => {
    useConnection.getState().setHub('connected')
    callbacks?.onConnected(true)
  })
  conn.onclose(() => {
    if (replaced.has(conn)) return
    useConnection.getState().setHub('disconnected')
    // Automatic reconnect gave up or the server closed the connection: start again.
    if (!stopped) void startHub()
  })
  callbacks?.register(conn)
  return conn
}

/** A wait that reconnectNow can end early. */
function sleep(ms: number) {
  return new Promise<void>((resolve) => {
    const timer = setTimeout(done, ms)
    function done() {
      clearTimeout(timer)
      if (wake === done) wake = null
      resolve()
    }
    wake = done
  })
}

export function configureHub(next: HubCallbacks): void {
  callbacks = next
}

/**
 * Start (or keep starting) the connection. Resolves after the first attempt, so callers can
 * continue with sync; retries continue in the background forever.
 */
export function startHub(): Promise<void> {
  stopped = false
  if (!connection) connection = build()
  const conn = connection
  if (conn.state === HubConnectionState.Connected || conn.state === HubConnectionState.Reconnecting) {
    return Promise.resolve()
  }
  if (startLoop) return Promise.resolve()

  const signal: { resolve: (() => void) | null } = { resolve: null }
  const first = new Promise<void>((resolve) => {
    signal.resolve = resolve
  })

  startLoop = (async () => {
    let attempt = 0
    while (!stopped && conn.state === HubConnectionState.Disconnected) {
      useConnection.getState().setHub('connecting')
      try {
        await conn.start()
        useConnection.getState().setHub('connected')
        signal.resolve?.()
        callbacks?.onConnected(false)
        return
      } catch {
        useConnection.getState().setHub('disconnected')
        signal.resolve?.()
        signal.resolve = null
        const delay = foreverRetryPolicy.nextRetryDelayInMilliseconds({
          previousRetryCount: attempt++,
          elapsedMilliseconds: 0,
          retryReason: new Error('start failed'),
        })
        await sleep(Math.max(delay ?? 30000, 1000))
      }
    }
    signal.resolve?.()
  })().finally(() => {
    startLoop = null
  })

  return first
}

/**
 * The app is in front again, or the network is back: be connected now rather than after a backoff, or after the
 * server timeout reveals a socket that died while the app was away. `trustConnected` is false when the app was hidden
 * long enough (STALE_AFTER_HIDDEN_MS) or the network changed.
 */
export async function reconnectNow(trustConnected: boolean): Promise<void> {
  if (stopped) return
  const conn = connection
  const action = resumeAction(conn?.state ?? null, trustConnected)
  if (action === 'none') return
  if (action === 'wake') {
    wake?.()
    return
  }
  replaced.add(conn!)
  connection = null
  useConnection.getState().setHub('reconnecting')
  await conn!.stop().catch(() => undefined)
  await startHub()
}

export async function stopHub(): Promise<void> {
  stopped = true
  const conn = connection
  connection = null
  if (conn) await conn.stop().catch(() => undefined)
  useConnection.getState().setHub('idle')
}

export function hubConnected(): boolean {
  return connection?.state === HubConnectionState.Connected
}

/** Fire-and-forget hub invocation; silently skipped while disconnected. */
export async function hubInvoke(method: string, ...args: unknown[]): Promise<boolean> {
  if (!connection || connection.state !== HubConnectionState.Connected) return false
  try {
    await connection.invoke(method, ...args)
    return true
  } catch (error) {
    console.warn(`hub ${method} failed`, error)
    return false
  }
}

export const hub = {
  subscribe: (channelId: string) => hubInvoke('Subscribe', channelId),
  unsubscribe: (channelId: string) => hubInvoke('Unsubscribe', channelId),
  typing: (channelId: string) => hubInvoke('Typing', channelId),
  markRead: (channelId: string, seq: number) => hubInvoke('MarkRead', channelId, seq),
  setFocused: (channelId: string | null) => hubInvoke('SetFocused', channelId),
}
